import { VideoEventDescription } from '$Components/Shared/VideoEventDescription';
import VideoEventFlagButton from '$Components/VideoEvents/VideoEventFlagButton/VideoEventFlagButton';
import { React, moment, unit } from 'Imports';
import DetailsField from './DetailsField';
import { UserResponse, VideoEventResponse } from '$Generated/api';
import { useState, useMemo, useEffect } from 'react';
import EditEventDriverDialog from './EditEventDriverDialog';
import ShareEventDialog from './ShareEventDialog';
import ExportEventDialog from './ExportEventDialog';
import { Divider } from 'MaterialUIComponents';
import { isEncompass } from '$Utilities/integrationPlatform';
import * as DateFormatter from '$Components/Shared/DateFormatter';
import { getStatusLabel } from '$Utilities/videoEventStatusUtility';
import ActionButton from './ActionButton';
import { faFileExcel, faFileExport, faShare, faShareSquare, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { IntegrationPartnerDataService } from '$State/IntegrationPartnerDataFreezerService';
import { IVideoEventServiceInjectedProps, VideoEventService } from '$State/VideoEventFreezerService';
import { DeleteConfirmation } from '$Components/Shared/DeleteConfirmation';
import { canEditByRole, RolesEnum } from '$Externals/VerifyRole';
import { useVideoEventExport } from '$Components/VideoEvents/VideoEventExport/useVideoEventExport';
import { splitTreatmentNames, useGetSplitTreatment } from '$Utilities/split-io-sdk';

interface VideoEventDetailsSectionProps {
    videoEvent: VideoEventResponse;
    userCanEdit: boolean;
    onVideoEventUpdated: () => void;
    onDelete: () => void;
    onUndoDelete: () => void;
    showLocationMapTab?: boolean;
    onLocationClick?: () => void;
    locationLinkUrl?: string;
}

const styles = require('./DetailsSection.scss') as {
    flagAndTitleSection: string;
    divider: string;
    dataSectionGeotab: string;
    dataSectionEncompass: string;
    actionsSection: string;
    editDriverEncompass: string;
    editDriverGeotab: string;
    locationLink: string;
    locationLinkGeotab: string;
};

const _VideoEventDetailsSection = (props: VideoEventDetailsSectionProps & IVideoEventServiceInjectedProps) => {
    const [flaggedByUsers, setFlaggedByUsers] = useState<UserResponse[]>(props.videoEvent.flaggedByUsers ?? []);
    const [isEditDriverDialogOpen, setIsEditDriverDialogOpen] = useState(false);
    const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
    const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [currentDriver, setCurrentDriver] = useState<{ integrationId: string; email: string; phoneNumber: string; fullName: string }>({
        integrationId: props.videoEvent.driverIntegrationPartnerSourceId || '',
        email: props.videoEvent.driverEmail || '',
        phoneNumber: props.videoEvent.driverPhoneNumber || '',
        fullName: props.videoEvent.driverName || '',
    });
    const { canExport } = useVideoEventExport();
    const userCanDelete = canEditByRole([RolesEnum.videoAdmin]);
    const [videoEventDeleteSplitOn, setVideoEventDeleteSplitOn] = useState(false);

    const { getSplitTreatment } = useGetSplitTreatment();

    const getSpeedInfo = (event: VideoEventResponse) => {
        const isMetric = IntegrationPartnerDataService.getUserIsMetric();

        // Event Speed Info
        const hasStartSpeed = event.kphStart || event.kphStart === 0;
        const hasEndSpeed = event.kphEnd || event.kphEnd === 0;

        const startSpeed = hasStartSpeed ? unit.getSpeedValue(event.kphStart, isMetric) : null;
        const endSpeed = hasEndSpeed ? unit.getSpeedValue(event.kphEnd, isMetric) : null;
        const speedChange = hasStartSpeed && hasEndSpeed ? Math.abs(Math.round(endSpeed || 0) - Math.round(startSpeed || 0)) : null;

        const speedUnitString = unit.getSpeedUnitString(isMetric, false);

        // em dash unicode: \u2014
        const startSpeedFormatted = startSpeed !== null ? `${unit.formatSpeedString(startSpeed)} ${speedUnitString}` : '\u2014';
        const endSpeedFormatted = endSpeed !== null ? `${unit.formatSpeedString(endSpeed)} ${speedUnitString}` : '\u2014';
        const speedChangeFormatted = speedChange !== null ? `${unit.formatSpeedString(speedChange)} ${speedUnitString}` : '\u2014';

        // Posted Speed Limit Info
        const speedLimitEventId = 29;
        const criticalSpeedEventId = 34;
        const json = event.eventJson;
        let postedSpeedFormatted = '';

        if (
            json &&
            (event.videoEventType?.indexOf(speedLimitEventId) !== -1 || event.videoEventType?.indexOf(criticalSpeedEventId) !== -1)
        ) {
            let eventJson = JSON.parse(json);

            try {
                // Speed Limit is currently stored only in the EventJson which is an arbitrary JSON that comes from the TelematicProvider
                let speedLimit = 0;
                //Attempt to pull speed limit from Streamax json
                const eventSource = eventJson.filter(
                    (e: any) => e['TelematicRuleName'] == '56007' || e['TelematicRuleName'] == 'CRITICALSPEEDING',
                )[0]['Source'];

                speedLimit = JSON.parse(JSON.stringify(eventSource).toLocaleLowerCase())['alarmadditionalinfo']['speedlimit'];
                postedSpeedFormatted = `${isMetric ? Math.round(unit.mphToKph(speedLimit)) : speedLimit} ${unit.getSpeedUnitString(isMetric, false)}`;
            } catch {
                postedSpeedFormatted = '';
            }
        }

        return {
            startSpeed: startSpeedFormatted,
            endSpeed: endSpeedFormatted,
            speedChange: speedChangeFormatted,
            postedSpeed: postedSpeedFormatted,
        };
    };

    useEffect(() => {
        const getSplitStatus = async () => {
            setVideoEventDeleteSplitOn(await getSplitTreatment(splitTreatmentNames.videoEventDelete));
        };

        getSplitStatus().catch(() => {
            setVideoEventDeleteSplitOn(false);
        });
    }, []);

    const liveStatus = props.videoEvents.getState().selectedVideoEventResult.data?.videoEventStatus ?? props.videoEvent.videoEventStatus;
    const liveScore = props.videoEvents.getState().selectedVideoEventResult.data?.score ?? props.videoEvent.score;
    const isClosed = liveStatus === 'Completed' || liveStatus === 'Dismissed';
    const eventStatusLabel = getStatusLabel(liveStatus!);
    const eventDate = props.videoEvent.eventStartDate ? moment(props.videoEvent.eventStartDate) : moment();
    const { startSpeed, endSpeed, speedChange, postedSpeed } = getSpeedInfo(props.videoEvent);
    const locationStreet = `${props.videoEvent.addressStreetNumber || ''} ${props.videoEvent.addressStreetName || ''}`.trim();
    const locationCityLine =
        `${props.videoEvent.addressCityName ? props.videoEvent.addressCityName + ', ' : ''}${props.videoEvent.addressRegion || ''} ${props.videoEvent.addressPostalCode || ''}`.trim();
    const locationText = (
        <>
            {locationStreet}
            {locationStreet && locationCityLine && <br />}
            {locationCityLine}
        </>
    );

    const handleFlagButtonClick = async (videoEventId: number) => {
        await VideoEventService.flagVideoEvent(videoEventId);
        props.onVideoEventUpdated();

        const currentUser = IntegrationPartnerDataService.getState().currentUserResult.data;
        const flaggedByCurrentUser = flaggedByUsers.some((user) => user.integrationProviderSourceId === currentUser.id);

        if (flaggedByCurrentUser) {
            setFlaggedByUsers(flaggedByUsers.filter((user) => user.integrationProviderSourceId !== currentUser.id));
        } else {
            setFlaggedByUsers((prev) => [
                ...prev,
                {
                    id: '',
                    integrationProviderSourceId: currentUser.id,
                    email: currentUser.email,
                    displayName: `${currentUser.firstName} ${currentUser.lastName}`,
                } as UserResponse,
            ]);
        }
    };

    const videoEventWithCurrentDriver = useMemo(
        () => ({ ...props.videoEvent, driverEmail: currentDriver.email, driverPhoneNumber: currentDriver.phoneNumber }),
        [props.videoEvent, currentDriver.email, currentDriver.phoneNumber],
    );

    const handleEditDriverClick = () => {
        setIsEditDriverDialogOpen(true);
    };

    const handleExportSuccess = () => {
        setIsExportDialogOpen(false);
        props.onVideoEventUpdated();
    };

    const handleDriverUpdated = (integrationId: string, fullName: string, email: string, phoneNumber: string) => {
        setCurrentDriver({
            integrationId: integrationId,
            email: email,
            phoneNumber: phoneNumber,
            fullName: fullName,
        });
        props.onVideoEventUpdated();
        setIsEditDriverDialogOpen(false);
    };

    return (
        <>
            <div className={styles.flagAndTitleSection}>
                <div>
                    <VideoEventFlagButton
                        videoEventId={props.videoEvent.id ?? 0}
                        flaggedByUsers={flaggedByUsers}
                        showLabel={true}
                        userCanEdit={props.userCanEdit}
                        onClick={(videoEventId) => handleFlagButtonClick(videoEventId)}
                    ></VideoEventFlagButton>
                </div>
                <Divider className={styles.divider} />
                <div>
                    <VideoEventDescription
                        videoEventTypePairings={props.videoEvent.videoEventTypePairings}
                        videoEventTypeString={props.videoEvent.videoEventTypeString}
                        videoEventFontSize="18px"
                        videoEventFontWeight={isEncompass ? '700' : '500'}
                    />
                </div>
            </div>
            <div className={isEncompass ? styles.dataSectionEncompass : styles.dataSectionGeotab}>
                <DetailsField label="Event Status">{eventStatusLabel}</DetailsField>

                <DetailsField label="Date/Time">
                    {DateFormatter.date(eventDate)}
                    <br />
                    {DateFormatter.timeWithSecondsAndTimezone(eventDate)}
                </DetailsField>

                <DetailsField label="Location">
                    {locationStreet || locationCityLine ? (
                        props.showLocationMapTab ? (
                            <a
                                href="#"
                                className={styles.locationLink}
                                onClick={(e) => {
                                    e.preventDefault();
                                    props.onLocationClick?.();
                                }}
                            >
                                {locationText}
                            </a>
                        ) : props.locationLinkUrl ? (
                            <a href={props.locationLinkUrl} className={styles.locationLinkGeotab}>
                                {locationText}
                            </a>
                        ) : (
                            locationText
                        )
                    ) : (
                        '\u2014'
                    )}
                </DetailsField>

                <DetailsField label="Driver">
                    {currentDriver.fullName.trim() || 'Unknown'}
                    {props.userCanEdit && (
                        <span
                            className={isEncompass ? styles.editDriverEncompass : styles.editDriverGeotab}
                            onClick={handleEditDriverClick}
                        >
                            Edit
                        </span>
                    )}
                </DetailsField>

                <DetailsField label="Vehicle">{props.videoEvent.vehicleDescription || 'N/A'}</DetailsField>

                <DetailsField label="Camera Serial #">{props.videoEvent.cameraDeviceSerialNumber}</DetailsField>

                <DetailsField label={isEncompass ? 'Event Speed' : 'Start Speed'}>{startSpeed}</DetailsField>

                {!isEncompass && (
                    <>
                        <DetailsField label="End Speed">{endSpeed}</DetailsField>

                        <DetailsField label="Change Speed">{speedChange}</DetailsField>
                    </>
                )}

                {postedSpeed && (
                    <>
                        <DetailsField label="Posted Speed">{postedSpeed}</DetailsField>
                    </>
                )}

                <DetailsField label="Event ID">{props.videoEvent.displayId}</DetailsField>
            </div>
            <div className={styles.actionsSection}>
                <ActionButton
                    icon={isEncompass ? faFileExcel : faFileExport}
                    text="Export"
                    onClick={() => setIsExportDialogOpen(true)}
                    disabled={!canExport}
                />
                <ActionButton
                    icon={isEncompass ? faShare : faShareSquare}
                    text="Share"
                    onClick={() => setIsShareDialogOpen(true)}
                    disabled={!props.userCanEdit || isClosed}
                />

                {videoEventDeleteSplitOn && (
                    <ActionButton icon={faTrashAlt} text="Delete" onClick={() => setIsDeleteDialogOpen(true)} disabled={!userCanDelete} />
                )}
            </div>

            <EditEventDriverDialog
                isOpen={isEditDriverDialogOpen}
                currentDriverIntegrationId={currentDriver.integrationId}
                onClose={() => setIsEditDriverDialogOpen(false)}
                onDriverUpdated={handleDriverUpdated}
                videoEvent={props.videoEvent}
            />

            <ShareEventDialog
                open={isShareDialogOpen}
                videoEvent={videoEventWithCurrentDriver}
                onClose={() => setIsShareDialogOpen(false)}
                onSuccess={() => {
                    setIsShareDialogOpen(false);
                    props.videoEvents.updateLocalVideoEventStatus('Sent_To_Driver', props.videoEvent.id);
                    props.onVideoEventUpdated();
                }}
            />

            <ExportEventDialog
                open={isExportDialogOpen}
                videoEvent={{
                    ...props.videoEvent,
                    driverName: currentDriver.fullName,
                    driverEmail: currentDriver.email,
                    driverPhoneNumber: currentDriver.phoneNumber,
                    videoEventStatus: liveStatus,
                    flaggedByUsers: flaggedByUsers,
                    isFlagged: flaggedByUsers.length > 0,
                    score: liveScore,
                }}
                onClose={() => setIsExportDialogOpen(false)}
                onSuccess={handleExportSuccess}
            />

            <DeleteConfirmation
                showDeletePopup={isDeleteDialogOpen}
                toggleDeletePopup={() => setIsDeleteDialogOpen(!isDeleteDialogOpen)}
                changeToDisabled={() => null}
                eventIds={props.videoEvent.id ? [props.videoEvent.id] : []}
                onDelete={props.onDelete}
                onUndoDelete={props.onUndoDelete}
                skipUpdateListOnSuccess={true}
            />
        </>
    );
};

const VideoEventDetailsSection = IntegrationPartnerDataService.inject(VideoEventService.inject(_VideoEventDetailsSection));

export default VideoEventDetailsSection;
